// auth.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { generateCodeChallenge, generateCodeVerifier } from './pkce.util';
import { AuthConfig, OauthConfigService } from './oauth-config.service';

@Injectable({ providedIn: 'root' })
export class AuthService {

    private popupWindow: Window | null = null;
    private authMessageHandler: ((event: MessageEvent) => void) | null = null;

    constructor(private http: HttpClient,
                private oauthConfigService: OauthConfigService) {
    }

    // ----------------------------------------------------------------
    // Step 1 — Open popup and redirect to authorization endpoint
    // ----------------------------------------------------------------
    async login(issuer: string): Promise<string> {

        const authConfig = await this.oauthConfigService.get();

        authConfig.issuer = issuer;

        const discoveryDoc = await this.fetchDiscoveryDocument(issuer);

        const verifier = await generateCodeVerifier();
        const challenge = await generateCodeChallenge(verifier);
        const state = this.randomState();

        // Persist for retrieval after redirect
        sessionStorage.setItem('pkce_verifier', verifier);
        sessionStorage.setItem('pkce_state', state);
        sessionStorage.setItem('auth_issuer', issuer);
        sessionStorage.setItem('auth_endpoint_token', discoveryDoc.token_endpoint);

        const params = new HttpParams()
            .set('response_type', 'code')
            .set('client_id', authConfig.clientId)
            .set('redirect_uri', authConfig.redirectUri)
            .set('scope', authConfig.scope)
            .set('state', state)
            .set('code_challenge', challenge)
            .set('code_challenge_method', 'S256');

        const authUrl = `${discoveryDoc.authorization_endpoint}?${params.toString()}`;

        return new Promise<string>((resolve, reject) => {
            this.openPopup(authUrl);

            // Remove any previously registered handler before adding a new one
            if (this.authMessageHandler) {
                window.removeEventListener('message', this.authMessageHandler);
                this.authMessageHandler = null;
            }

            // Listen for the callback message posted by the popup
            const handler = (event: MessageEvent) => {
                if (event.origin !== window.location.origin) {
                    return;
                }
                if (event.data?.type !== 'AUTH_CALLBACK') {
                    return;
                }

                window.removeEventListener('message', this.authMessageHandler!);
                this.authMessageHandler = null;

                const { code, state: returnedState, error } = event.data;

                if (error) {
                    reject(new Error(error));
                    return;
                }

                if (returnedState !== sessionStorage.getItem('pkce_state')) {
                    reject(new Error('State mismatch — possible CSRF'));
                    return;
                }

                this.exchangeCodeForToken(code, authConfig)
                    .then(resolve)
                    .catch(reject);
            };

            this.authMessageHandler = handler;
            window.addEventListener('message', this.authMessageHandler);
        });
    }

    // ----------------------------------------------------------------
    // Step 2 — Exchange authorization code for access token
    // ----------------------------------------------------------------
    private exchangeCodeForToken(code: string, authConfig: AuthConfig): Promise<string> {
        const tokenEndpoint = sessionStorage.getItem('auth_endpoint_token')!;
        const verifier = sessionStorage.getItem('pkce_verifier')!;

        const body = new HttpParams()
            .set('grant_type', 'authorization_code')
            .set('client_id', authConfig.clientId)
            .set('redirect_uri', authConfig.redirectUri)
            .set('code', code)
            .set('code_verifier', verifier);

        return this.http.post<any>(tokenEndpoint, body.toString(), {
            headers: new HttpHeaders({
                'Content-Type': 'application/x-www-form-urlencoded'
            })
        })
            .toPromise()
            .then(response => {
                this.storeTokens(response);
                this.clearPkceStorage();
                return response.access_token;
            });
    }

    // ----------------------------------------------------------------
    // Helpers
    // ----------------------------------------------------------------
    private fetchDiscoveryDocument(issuer: string): Promise<any> {
        const url = `${issuer}/.well-known/openid-configuration`;
        return this.http.get<any>(url).toPromise();
    }

    private openPopup(url: string): void {
        const width = 500;
        const height = 600;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;

        this.popupWindow = window.open(
            url,
            'oidc-login-popup',
            `width=${width},height=${height},left=${left},top=${top}`
        );
    }

    private storeTokens(response: any): void {
        sessionStorage.setItem('access_token', response.access_token);
        sessionStorage.setItem('id_token', response.id_token ?? '');
        sessionStorage.setItem('refresh_token', response.refresh_token ?? '');
        sessionStorage.setItem('token_expires_at',
            String(Date.now() + response.expires_in * 1000)
        );
    }

    private clearPkceStorage(): void {
        sessionStorage.removeItem('pkce_verifier');
        sessionStorage.removeItem('pkce_state');
        sessionStorage.removeItem('auth_endpoint_token');
    }

    private randomState(): string {
        const array = new Uint8Array(16);
        crypto.getRandomValues(array);
        return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
    }

    getAccessToken(): string | null {
        return sessionStorage.getItem('access_token');
    }

    isTokenExpired(): boolean {
        const expiresAt = sessionStorage.getItem('token_expires_at');
        if (!expiresAt) {
            return true;
        }
        return Date.now() > parseInt(expiresAt, 10);
    }

    logout(): void {
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('id_token');
        sessionStorage.removeItem('refresh_token');
        sessionStorage.removeItem('token_expires_at');
        sessionStorage.removeItem('auth_issuer');
    }
}
